import { createApi } from '@reduxjs/toolkit/query/react'
import { handleError } from '../../commons/utils/utils'
import { setFeatureFlagInfo } from '../slices/featureFlagSlice'
import { generateRPCQuery, getBaseQuery } from './apiUtils'
import apiConstants from './constants'

export const featureFlagsApi = createApi({
  reducerPath: 'featureFlagsApi',
  refetchOnFocus: false,
  baseQuery: getBaseQuery(),
  tagTypes: ['FeatureInfo'],
  endpoints: (builder) => ({
    getFeatureFlags: builder.query({
      query: (requestParams: CommonSearchParams) =>
        generateRPCQuery(
          apiConstants.FEATURE_FLAGS_RPC_PATH,
          'retrieveFeatureFlags',
          requestParams
        )
    }),
    getFeatureFlagByID: builder.query({
      query: (requestParams: { FeatureFlagID: string }) =>
        generateRPCQuery(
          apiConstants.FEATURE_FLAGS_RPC_PATH,
          'getFeatureFlagByID',
          requestParams
        ),
      async onQueryStarted ({}, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          if (data && data.result) {
            dispatch(setFeatureFlagInfo(data.result))
          }
        } catch (err: any) {
          handleError(err, dispatch)
        }
      },
      providesTags: [{ type: 'FeatureInfo' }]
    }),
    updateFeatureFlagStatusByID: builder.mutation({
      query: (requestParams: UpdateFeatureFlagStatusParams) =>
        generateRPCQuery(
          apiConstants.FEATURE_FLAGS_RPC_PATH,
          'updateFeatureFlagStatusByID',
          requestParams
        )
    }),
    removeFeatureFlagFromGroup: builder.mutation({
      query: (requestParams: CommonFeatureFlagToGroupParams) =>
        generateRPCQuery(
          apiConstants.FEATURE_FLAGS_RPC_PATH,
          'removeFeatureFlagFromGroup',
          requestParams
        )
    }),
    addFeatureFlagToGroup: builder.mutation({
      query: (requestParams: CommonFeatureFlagToGroupParams) =>
        generateRPCQuery(
          apiConstants.FEATURE_FLAGS_RPC_PATH,
          'addFeatureFlagToGroup',
          requestParams
        )
    }),
    validateFeatureFlagInformation: builder.mutation({
      query: (requestParams: UpdateFeatureFlagInfoParams) =>
        generateRPCQuery(
          apiConstants.FEATURE_FLAGS_RPC_PATH,
          'validateFeatureFlagInformation',
          requestParams
        )
    }),
    updateFeatureFlagInfoByID: builder.mutation({
      query: (requestParams: UpdateFeatureFlagInfoParams) =>
        generateRPCQuery(
          apiConstants.FEATURE_FLAGS_RPC_PATH,
          'updateFeatureFlagInfoByID',
          requestParams
        ),
      invalidatesTags: [{ type: 'FeatureInfo' }]
    })
  })
})

export const {
  useGetFeatureFlagsQuery,
  useGetFeatureFlagByIDQuery,
  useUpdateFeatureFlagStatusByIDMutation,
  useRemoveFeatureFlagFromGroupMutation,
  useAddFeatureFlagToGroupMutation,
  useValidateFeatureFlagInformationMutation,
  useUpdateFeatureFlagInfoByIDMutation
} = featureFlagsApi
